import React from 'react';
import { useAppDispatch, useAppSelector } from '@redux/hooks';

import { Box, Button, Paper, SxProps, Theme, Typography } from '@mui/material';
import QueryBuilderRoundedIcon from '@mui/icons-material/QueryBuilderRounded';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';

import {
  CandidateDetailsAction,
  CandidateListingStatusLabel,
  CandidateStatus,
  hasPermission,
} from '@constants';
import { ICandidateDetails } from '@types';
import { setCandidateDetailsAction } from '@redux/candidateDetails';
import { buttonSizeSx, secondaryButtonSx } from 'theme/buttons';
import { CandidateActionsMenu } from '../CandidateActionsMenu/CandidateActionsMenu';
import { selectPermissions } from '@redux/accounts/accounts.selectors';
import {
  bookedSlotAt,
  isInterviewOverdue,
  normalizeCandidateStatus,
} from '@utils';
import { logEvent } from '@services';

interface ICandidateDetailsActionButtonProps {
  data: ICandidateDetails;
  sx?: SxProps<Theme>;
}

const DECISION_SX = { ...buttonSizeSx.md, ...secondaryButtonSx } as const;

const BarNote: React.FC<{ icon?: boolean; children: React.ReactNode }> = ({
  icon,
  children,
}) => (
  <Box display="flex" alignItems="center" gap={0.75} color="text.secondary">
    {icon && <QueryBuilderRoundedIcon sx={{ fontSize: 16 }} />}
    <Typography variant="body1" color="text.secondary">
      {children}
    </Typography>
  </Box>
);

export const CandidateDetailsActionButton: React.FC<
  ICandidateDetailsActionButtonProps
> = ({ data, sx }) => {
  const dispatch = useAppDispatch();
  const canUpdateCandidate = hasPermission(
    useAppSelector(selectPermissions),
    'H:W',
  );
  const candidateStatus = normalizeCandidateStatus(data.status);

  const handleActionButtonClick = (action: CandidateDetailsAction) => {
    dispatch(setCandidateDetailsAction(action));
    if (
      action === CandidateDetailsAction.SKIP_CANDIDATE ||
      action === CandidateDetailsAction.CLIENT_INTERVIEW_FAILED
    ) {
      logEvent('candidate_skipped');
    }
    if (
      action === CandidateDetailsAction.APPROVE_CANDIDATE ||
      action === CandidateDetailsAction.CLIENT_INTERVIEW_PASSED
    ) {
      logEvent('candidate_approved');
    }
  };

  const latestRound = data.interview?.at(-1) || null;
  const booked = bookedSlotAt(
    latestRound?.scheduledSlot,
    latestRound?.clientTimezone,
  );
  const overdue = isInterviewOverdue(
    data.status,
    latestRound?.scheduledSlot,
    latestRound?.clientTimezone,
  );

  const action = (
    label: string,
    icon: React.ReactNode,
    candidateAction: CandidateDetailsAction,
    primary?: boolean,
  ) => (
    <Button
      disabled={!canUpdateCandidate}
      variant={primary ? 'contained' : 'outlined'}
      sx={primary ? buttonSizeSx.md : DECISION_SX}
      startIcon={icon}
      onClick={() => handleActionButtonClick(candidateAction)}
    >
      {label}
    </Button>
  );

  const { controls, note, primaryActions } = ((): {
    controls: React.ReactNode;
    note?: React.ReactNode;
    primaryActions: CandidateDetailsAction[];
  } => {
    switch (candidateStatus) {
      case CandidateStatus.Submitted:
        return {
          controls: (
            <>
              {action(
                'Schedule interview',
                <EventOutlinedIcon sx={{ fontSize: 14 }} />,
                CandidateDetailsAction.SCHEDULE_INTERVIEW,
                true,
              )}
              {action(
                'Skip candidate',
                <ThumbDownOutlinedIcon sx={{ fontSize: 14 }} />,
                CandidateDetailsAction.SKIP_CANDIDATE,
              )}
            </>
          ),
          primaryActions: [
            CandidateDetailsAction.SCHEDULE_INTERVIEW,
            CandidateDetailsAction.SKIP_CANDIDATE,
          ],
        };

      case CandidateStatus.InterviewDone:
        return {
          controls: (
            <>
              {action(
                'Proceed to hiring',
                <ThumbUpOutlinedIcon sx={{ fontSize: 14 }} />,
                CandidateDetailsAction.CLIENT_INTERVIEW_PASSED,
              )}
              {action(
                'Skip candidate',
                <ThumbDownOutlinedIcon sx={{ fontSize: 14 }} />,
                CandidateDetailsAction.CLIENT_INTERVIEW_FAILED,
              )}
            </>
          ),
          primaryActions: [
            CandidateDetailsAction.CLIENT_INTERVIEW_PASSED,
            CandidateDetailsAction.CLIENT_INTERVIEW_FAILED,
          ],
        };

      case CandidateStatus.CallScheduled:
        return overdue
          ? {
              controls: action(
                'Reschedule interview',
                <EventRepeatOutlinedIcon sx={{ fontSize: 14 }} />,
                CandidateDetailsAction.RESCHEDULE_INTERVIEW,
                true,
              ),
              note: booked && (
                <BarNote icon>
                  The slot on {booked.format('ddd, MMM DD, hh:mm a')} has passed
                </BarNote>
              ),
              primaryActions: [CandidateDetailsAction.RESCHEDULE_INTERVIEW],
            }
          : {
              controls: null,
              note: (
                <BarNote icon>
                  {booked
                    ? `Interview booked for ${booked.format('ddd, MMM DD, hh:mm a')}`
                    : 'Interview scheduled'}
                </BarNote>
              ),
              primaryActions: [],
            };

      case CandidateStatus.CallCancelled:
        return {
          controls: action(
            'Reschedule interview',
            <EventRepeatOutlinedIcon sx={{ fontSize: 14 }} />,
            CandidateDetailsAction.RESCHEDULE_INTERVIEW,
            true,
          ),
          note: <BarNote>This interview was cancelled</BarNote>,
          primaryActions: [CandidateDetailsAction.RESCHEDULE_INTERVIEW],
        };

      case CandidateStatus.CallRequested:
        return {
          controls: null,
          note: (
            <BarNote icon>
              Waiting for AgileEngine to confirm a time with the candidate
            </BarNote>
          ),
          primaryActions: [],
        };

      default:
        return {
          controls: null,
          note: candidateStatus && (
            <BarNote>
              {[
                CandidateListingStatusLabel[candidateStatus],
                data.closingReason,
              ]
                .filter(Boolean)
                .join(' — ')}
            </BarNote>
          ),
          primaryActions: [],
        };
    }
  })();

  return (
    <Paper
      sx={[
        (theme) => ({
          px: 3,
          py: 2,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: { sm: 'wrap' },
          alignItems: { sm: 'center' },
          gap: 1,
          borderRadius: 0,
          borderTop: `1px solid ${theme.palette.background.divider}`,
          boxShadow: 'none',
          '& > *': { width: { xs: '100%', sm: 'auto' } },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {controls}
      {note}
      <CandidateActionsMenu
        data={data}
        primaryActions={primaryActions}
        onAction={handleActionButtonClick}
      />
    </Paper>
  );
};
